(() => {
  if (customElements.get('hemkind-colors')) return;

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();
  const exactVariant = (choice, size) => choice.variants.find((variant) => variant.size === size);

  function buildFamily(data) {
    if (!data || !Array.isArray(data.products) || data.products.length < 2) return null;
    const current = data.products.find((product) => product.id === data.productId);
    if (!current) return null;
    const choices = [];
    const productIds = new Set();
    const colorOwners = new Map();
    for (const product of data.products) {
      if (productIds.has(product.id) || product.options?.length !== 2 || ![0, 1].includes(product.colorIndex) || ![0, 1].includes(product.sizeIndex) || product.colorIndex === product.sizeIndex || !['color', 'colour', 'kleur'].includes(normalize(product.options[product.colorIndex])) || !['size', 'maat'].includes(normalize(product.options[product.sizeIndex])) || !Array.isArray(product.variants) || product.variants.length !== product.variantCount || !product.variants.length) return null;
      productIds.add(product.id);
      const colors = new Map();
      for (const variant of product.variants) {
        if (!variant.id || !normalize(variant.color) || !normalize(variant.size) || typeof variant.available !== 'boolean') return null;
        const key = normalize(variant.color);
        if (colorOwners.has(key) && colorOwners.get(key) !== product.id) return null;
        colorOwners.set(key, product.id);
        if (!colors.has(key)) colors.set(key, { product, color: variant.color, variants: [] });
        const choice = colors.get(key);
        if (choice.variants.some((item) => item.size === variant.size)) return null;
        choice.variants.push(variant);
      }
      choices.push(...colors.values());
    }
    return { current, choices };
  }

  class HemkindColors extends HTMLElement {
    connectedCallback() {
      this.version = (this.version || 0) + 1;
      this.onReady = () => this.initialize();
      document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      window.addEventListener('glozinspeed-allScriptsLoaded', this.onReady);
      queueMicrotask(this.onReady);
    }

    disconnectedCallback() {
      this.version += 1;
      clearTimeout(this.navigationTimer);
      document.removeEventListener('DOMContentLoaded', this.onReady);
      window.removeEventListener('glozinspeed-allScriptsLoaded', this.onReady);
      this.release();
    }

    release() {
      clearTimeout(this.navigationTimer);
      this.version += 1;
      this.hidden = true;
      this.scope?.removeEventListener('change', this.onNativeChange);
      this.scope?.removeEventListener('click', this.onNativeClick);
      this.observer?.disconnect();
      this.removeEventListener('click', this.onChoiceClick);
      this.removeEventListener('keydown', this.onChoiceKey);
      this.colorField?.removeAttribute('data-hemkind-native-color-hidden');
      if (this.picker?.hemkindColorsOwner === this) delete this.picker.hemkindColorsOwner;
      this.initialized = false;
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      try {
        this.data = JSON.parse(this.querySelector('[data-color-family]').textContent);
        this.family = buildFamily(this.data);
        this.scope = this.closest('[id^="MainProduct-"]');
        if (!this.family || this.scope?.id !== `MainProduct-${this.dataset.sectionId}`) return;
        this.picker = this.scope.querySelector('.main-product .product-detail__variant-picker variant-radios-detail, .main-product .product-detail__variant-picker variant-group-detail');
        this.form = document.getElementById(`product-form-${this.dataset.sectionId}`);
        if (!this.picker || !this.form || !this.scope.contains(this.form) || (this.picker.hemkindColorsOwner && this.picker.hemkindColorsOwner !== this)) return;
        if (!customElements.get(this.picker.localName)) {
          if (!this.waitingForPicker) {
            this.waitingForPicker = true;
            customElements.whenDefined(this.picker.localName).then(() => {
              this.waitingForPicker = false;
              this.initialize();
            });
          }
          return;
        }
        if (!this.findControls()) return;
        this.list = this.querySelector('[data-color-choices]');
        this.status = this.querySelector('[data-color-status]');
        this.selectedLabel = this.querySelector('[data-selected-color]');
        if (!this.list || !this.status || !this.selectedLabel) return;
        this.buildChoices();
        this.onNativeChange = (event) => {
          if (this.picker.contains(event.target) || this.form.contains(event.target)) {
            if (this.picker.contains(event.target) && !this.selectingNative) {
              clearTimeout(this.navigationTimer);
              this.version += 1;
            }
            queueMicrotask(() => this.sync());
          }
        };
        this.onNativeClick = (event) => {
          if (this.picker.contains(event.target)) queueMicrotask(() => this.sync());
        };
        this.onChoiceClick = (event) => this.choose(event);
        this.onChoiceKey = (event) => {
          if (event.key === 'Enter' && event.target.matches('[data-color-choice]:not([href])')) this.choose(event);
        };
        this.scope.addEventListener('change', this.onNativeChange);
        this.scope.addEventListener('click', this.onNativeClick);
        this.addEventListener('click', this.onChoiceClick);
        this.addEventListener('keydown', this.onChoiceKey);
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.picker, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked', 'value', 'disabled'] });
        this.observer.observe(this.form, { subtree: true, attributes: true, attributeFilter: ['value', 'disabled'] });
        this.picker.hemkindColorsOwner = this;
        this.initialized = true;
        this.sync();
      } catch {
        this.release();
      }
    }

    findControls() {
      const product = this.family.current;
      const colorName = product.options[product.colorIndex];
      const sizeName = product.options[product.sizeIndex];
      const inputs = Array.from(this.picker.querySelectorAll('input[type="radio"]'));
      this.colorInputs = inputs.filter((input) => input.name === colorName && input.getAttribute('form') === this.form.id);
      this.sizeInputs = inputs.filter((input) => input.name === sizeName && input.getAttribute('form') === this.form.id);
      const currentColors = new Set(product.variants.map((variant) => variant.color));
      const fieldsets = new Set(this.colorInputs.map((input) => input.closest('fieldset')));
      if (fieldsets.size !== 1 || fieldsets.has(null) || !this.sizeInputs.length || this.colorInputs.length !== currentColors.size || this.colorInputs.some((input) => !currentColors.has(input.value))) return false;
      const nextField = this.colorInputs[0].closest('fieldset');
      if (this.sizeInputs.some((input) => nextField.contains(input))) return false;
      if (this.colorField !== nextField) this.colorField?.removeAttribute('data-hemkind-native-color-hidden');
      this.colorField = nextField;
      return true;
    }

    buildChoices() {
      this.list.replaceChildren();
      this.links = this.family.choices.map((choice, index) => {
        const link = document.createElement('a');
        link.className = 'hemkind-colors__choice';
        link.dataset.colorChoice = String(index);
        const image = choice.variants.find((variant) => variant.image)?.image;
        if (image) {
          const img = document.createElement('img');
          img.src = image;
          img.alt = '';
          img.width = 36;
          img.height = 42;
          img.loading = 'lazy';
          link.append(img);
        }
        const label = document.createElement('span');
        label.textContent = choice.color;
        link.append(label);
        this.list.append(link);
        return link;
      });
    }

    variantURL(variant) {
      const url = new URL(variant.url, window.location.href);
      if (url.origin !== window.location.origin) throw new Error('Invalid product URL');
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.href;
    }

    selection() {
      return {
        color: this.colorInputs.find((input) => input.checked)?.value,
        size: this.sizeInputs.find((input) => input.checked)?.value
      };
    }

    sync() {
      if (!this.initialized || !this.isConnected) return;
      try {
        if (!this.findControls()) return this.release();
        const { color, size } = this.selection();
        this.selectedLabel.textContent = color || '';
        this.family.choices.forEach((choice, index) => {
          const link = this.links[index];
          const variant = exactVariant(choice, size);
          const active = choice.product.id === this.data.productId && choice.color === color;
          const available = Boolean(size && variant?.available);
          link.setAttribute('aria-current', active ? 'true' : 'false');
          link.setAttribute('aria-disabled', available ? 'false' : 'true');
          link.setAttribute('aria-label', `${choice.color}${active ? `, ${this.data.labels.selected}` : ''}${available ? '' : `, ${size ? this.data.labels.unavailable : this.data.labels.chooseSize}`}`);
          if (available) {
            link.href = this.variantURL(variant);
            link.removeAttribute('tabindex');
            link.removeAttribute('role');
          } else {
            link.removeAttribute('href');
            link.tabIndex = 0;
            link.setAttribute('role', 'link');
          }
        });
        this.colorField.setAttribute('data-hemkind-native-color-hidden', '');
        this.hidden = false;
        if (this.lastSize !== size) this.status.textContent = '';
        this.lastSize = size;
      } catch {
        this.release();
      }
    }

    choose(event) {
      const link = event.target.closest('[data-color-choice]');
      if (!link || !this.contains(link)) return;
      const choice = this.family.choices[Number(link.dataset.colorChoice)];
      if (!choice) return;
      clearTimeout(this.navigationTimer);
      const version = ++this.version;
      const { size } = this.selection();
      const variant = exactVariant(choice, size);
      if (!size || !variant?.available) {
        event.preventDefault();
        this.status.textContent = size ? this.data.labels.unavailable : this.data.labels.chooseSize;
        return;
      }
      this.status.textContent = '';
      link.href = this.variantURL(variant);
      if (choice.product.id !== this.data.productId || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button !== undefined && event.button !== 0)) return;
      const nativeInput = this.colorInputs.find((input) => input.value === choice.color);
      if (!nativeInput || nativeInput.disabled) return;
      event.preventDefault();
      this.selectingNative = true;
      nativeInput.click();
      this.selectingNative = false;
      this.sync();
      const confirmed = () => String(this.form.querySelector('input[name="id"]')?.value) === variant.id;
      if (confirmed()) return;
      this.navigationTimer = setTimeout(() => {
        if (this.isConnected && this.version === version && !confirmed()) window.location.assign(this.variantURL(variant));
      }, 500);
    }
  }

  HemkindColors.buildFamily = buildFamily;
  HemkindColors.exactVariant = exactVariant;
  customElements.define('hemkind-colors', HemkindColors);
})();

